import Control from '@/Controls/Control';
import ComponentManager from '@/Management/ComponentManager';
import SplitterViewModelT from '@/ViewModels/Controls/SplitterViewModel';
import SplitterPaneViewModelT from '@/ViewModels/Controls/SplitterPaneViewModel';
import SplitterPaneT from '@/Controls/SplitterPane';
import SplitterPaneDividerT from '@/Controls/SplitterPaneDivider';
import {DisposeBehavior} from '#/DefaultViewExtensionInterfaces';

export default class Splitter extends Control {

    private SplitterPane = ComponentManager.getControl<typeof SplitterPaneT>('SplitterPane');
    private SplitterPaneDivider = ComponentManager.getControl<typeof SplitterPaneDividerT>('SplitterPaneDivider');

    private jQueryControl: JQuery;
    private panes: SplitterPaneT[];
    private dividers: SplitterPaneDividerT[];

    public constructor(private viewModel: SplitterViewModelT, private element: JQuery) {
        super(viewModel, element);

        if (!element.length)
            throw new Error('Element needed for splitter!');

        this.panes = [];
        this.dividers = [];
    }

    public getControl(): JQuery {
        if (this.jQueryControl != null) {
            return this.jQueryControl;
        }

        this.jQueryControl = this.element;

        const $children = this.jQueryControl.children();

        if ($children.length != this.viewModel.panes.length) {
            throw new Error('Splitter pane elements are not matching their ViewModels!');
        }

        for (let i = 0; i < this.viewModel.panes.length; i++) {
            const vm = this.viewModel.panes[i];
            const $child = $children.eq(i);
            const pane = this.createPane($child, vm);
            const tuple = pane.getTuple();
            this.panes.push(pane);

            const dividerVM = this.viewModel.dividers.filter(d => d.attachedTo === vm)[0];
            if (!dividerVM)
                continue;

            const divider = new this.SplitterPaneDivider(dividerVM);
            const dividerTuple = divider.getTuple();
            this.dividers.push(divider);

            dividerTuple.control.insertBefore(tuple.control);
        }

        this.applyBindings(this.jQueryControl, {
            controlName: 'splitter',
            loader: this.viewModel.isBusy,
            controlIdent: this.identifier,
            tooltip: this.viewModel.tooltip,
            invalid: this.viewModel.invalidationText,
            locator: 'splitter',
            visible: this.viewModel.isVisible,
            styles: this.definePureComputed({
                read: () => {
                    return [this.viewModel.isHorizontal() ? 'horizontal' : 'vertical'];
                }
            }),
            states: this.definePureComputed({
                read: () => {
                    return [this.viewModel.isEnabled() ? 'enabled' : 'disabled'];
                }
            })
        });

        return this.jQueryControl;
    }

    private createPane(child: JQuery, viewModel: SplitterPaneViewModelT): SplitterPaneT {
        return new this.SplitterPane(viewModel, child);
    }

    protected getDisposeBehavior(): DisposeBehavior {
        return "Asynchronous";
    }
}

ComponentManager.setControl('Splitter', Splitter, 'RADPresentation.HTMLClient.DefaultViewExtensions');